import React from 'react';

interface StatusFlowProps {
  currentStatus: string;
  className?: string;
}

interface StatusStep {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
}

const StatusFlow: React.FC<StatusFlowProps> = ({ currentStatus, className = '' }) => {
  const statusOrder = [
    'Pendente',
    'Aguardando pedido',
    'Pedido digitado',
    'Aprovado',
    'Chegou',
    'Descarregando',
    'Finalizado'
  ];

  
  const statusConfig: Record<string, StatusStep> = {
    'Pendente': {
      id: 'Pendente',
      label: 'Solicitado', // <-- SÓ MUDA A EXIBIÇÃO!
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: 'bg-blue-500'
    },
    'Aguardando pedido': {
      id: 'Aguardando pedido',
      label: 'Aguardando pedido',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'bg-yellow-500'
    },
    'Pedido digitado': {
      id: 'Pedido digitado',
      label: 'Pedido digitado',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      color: 'bg-blue-500'
    },
    'Aprovado': {
      id: 'Aprovado',
      label: 'Aprovado',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'bg-green-500'
    },
    'Chegou': {
      id: 'Chegou',
      label: 'Chegou',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      color: 'bg-purple-500'
    },
    'Descarregando': {
      id: 'Descarregando',
      label: 'Descarregando',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      ),
      color: 'bg-orange-500'
    },
    'Finalizado': {
      id: 'Finalizado',
      label: 'Finalizado',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ),
      color: 'bg-emerald-500'
    }
  };

  const getCurrentIndex = () => {
    // Normalizar status para comparação
    const normalizedStatus = currentStatus?.toLowerCase().trim();
    
    // Mapeamento direto para todos os status conhecidos
    const statusMapping: Record<string, number> = {
      'pendente': 0,
      'solicitado': 0, // Mapeia solicitado para pendente (compatibilidade)
      'aguardando pedido': 1,
      'aguardando_pedido': 1,
      'pedido digitado': 2,
      'pedido_digitado': 2,
      'aprovado': 3,
      'chegou': 4,
      'descarregando': 5,
      'finalizado': 6
    };
    
    // Tentar mapeamento direto primeiro
    const mappedIndex = statusMapping[normalizedStatus];
    if (mappedIndex !== undefined) {
      return mappedIndex;
    }
    
    // Se não encontrar, tentar busca normal
    const index = statusOrder.findIndex(status => status.toLowerCase() === normalizedStatus);
    
    return index >= 0 ? index : 0; // Retornar 0 em vez de -1
  };

  const getStepStatus = (stepId: string) => {
    const currentIndex = getCurrentIndex();
    const stepIndex = statusOrder.findIndex(status => status.toLowerCase() === stepId.toLowerCase());

    if (stepIndex < currentIndex) {
      return 'completed';
    } else if (stepIndex === currentIndex) {
      return 'active';
    } else {
      return 'pending';
    }
  };

  const getStepColor = (stepId: string) => {
    const status = getStepStatus(stepId);
    const config = statusConfig[stepId];
    
    if (status === 'completed') {
      return config.color;
    } else if (status === 'active') {
      return config.color;
    } else {
      return 'bg-gray-300';
    }
  };

  const getStepTextColor = (stepId: string) => {
    const status = getStepStatus(stepId);
    
    if (status === 'completed') {
      return 'text-gray-700';
    } else if (status === 'active') {
      return 'text-gray-900 font-semibold';
    } else {
      return 'text-gray-400';
    }
  };

  const getIconColor = (stepId: string) => {
    const status = getStepStatus(stepId);
    
    if (status === 'completed') {
      return 'text-white';
    } else if (status === 'active') {
      return 'text-white';
    } else {
      return 'text-gray-500';
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-center justify-between relative">
        {/* Linha de progresso */}
        <div className="absolute left-0 right-0 top-5 h-1 bg-gray-200 -z-10">
          <div 
            className="h-full bg-green-500 transition-all duration-500"
            style={{ 
              width: `${(getCurrentIndex() / (statusOrder.length - 1)) * 100}%` 
            }}
          />
        </div>

        {/* Etapas */}
        {statusOrder.map((stepId, index) => {
          const config = statusConfig[stepId];
          const status = getStepStatus(stepId);
          const isPulsing = status === 'active';
          
          return (
            <div key={stepId} className="flex flex-col items-center flex-1">
              {/* Círculo com ícone */}
              <div
                className={`
                  relative w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                  ${getStepColor(stepId)}
                  ${isPulsing ? 'ring-4 ring-green-400 ring-opacity-50' : ''}
                `}
              >
                <div className={getIconColor(stepId)}>
                  {config.icon}
                </div>
                
                {/* Indicador de status ativo - bolinha pulsando */}
                {isPulsing && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping" />
                )}
              </div>

              {/* Label */}
              <div className={`mt-2 text-xs text-center max-w-[80px] ${getStepTextColor(stepId)}`}>
                {config.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Informações adicionais */}
      <div className="mt-4 text-center">
        <div className="text-sm font-medium text-gray-700">
          Status atual: <span className="text-green-600">{currentStatus}</span>
        </div>
        <div className="text-xs text-gray-500 mt-1">
          Etapa {getCurrentIndex() + 1} de {statusOrder.length}
        </div>
      </div>
    </div>
  );
};

export default StatusFlow;
